import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TAX_DISCLOSURE, SUPPORT_EMAIL, INSTAGRAM_URL, TIKTOK_URL, SITE_DOMAIN } from '../config';
import { formatMoney, formatDate } from '../lib/format';
import usePageMeta from '../hooks/usePageMeta';
import styles from './ThankYou.module.css';

const SHARE_TEXT = encodeURIComponent('I just gave to Revive Worship. Join me in supporting their mission:');
const SHARE_URL = encodeURIComponent(`https://${SITE_DOMAIN}`);

export default function ThankYou() {
  usePageMeta('Thank you — Revive Worship');

  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('We could not find your donation reference. If you just gave, check your email for confirmation.');
      return;
    }
    fetch(`/.netlify/functions/get-session?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lookup failed'))))
      .then(setSession)
      .catch(() => setError('We could not load your donation details, but your gift went through. You will receive a confirmation email shortly.'));
  }, [sessionId]);

  const token = session?.portal_token;
  const isRecurring = session?.gift_type === 'recurring';

  const viewGiving = () => {
    window.location.href = token ? `/giving?token=${encodeURIComponent(token)}` : '/giving';
  };

  const manageRecurring = async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/customer-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('no portal url');
      }
    } catch {
      setPortalLoading(false);
      window.alert(`We could not open the management portal. Please email ${SUPPORT_EMAIL} and we will help.`);
    }
  };

  const amount = session ? formatMoney(session.amount, session.currency) : '';
  const designation = session?.designation?.trim();
  const startsOn = isRecurring && session?.start_date ? formatDate(session.start_date) : null;

  return (
    <section className={styles.page}>
      <div className={styles.shell}>
        <span className={styles.label}>thank you</span>
        <h1 className={styles.title}>
          Your gift is <em>a gift to Him.</em>
        </h1>

        {session ? (
          <p className={styles.body}>
            {isRecurring ? (
              <>
                We set up your {amount} gift, {session.cadence_label || 'recurring'}
                {designation ? <>, directed toward <em>{designation}</em></> : ''}.{' '}
                {startsOn ? <>Your first gift is scheduled for <em>{startsOn}</em>.</> : 'Your first gift is on its way.'}{' '}
                A confirmation is heading to <em>{session.customer_email}</em>.
              </>
            ) : (
              <>
                We received your {amount} gift{designation ? <> directed toward <em>{designation}</em></> : ''}.{' '}
                A confirmation is on its way to <em>{session.customer_email}</em>.
              </>
            )}
          </p>
        ) : error ? (
          <p className={styles.body}>{error}</p>
        ) : (
          <p className={styles.body}>Confirming your gift…</p>
        )}

        <div className={styles.actions}>
          {token && (
            <button type="button" className={styles.primary} onClick={viewGiving}>
              view giving &amp; tax receipts <span aria-hidden="true">→</span>
            </button>
          )}
          {token && isRecurring && (
            <button type="button" className={styles.ghost} onClick={manageRecurring} disabled={portalLoading}>
              {portalLoading ? 'opening…' : 'manage recurring gift'}
            </button>
          )}
          <Link to="/" className={styles.ghost}>back to revive</Link>
        </div>

        <div className={styles.share}>
          <span className={styles.shareLabel}>tell a friend</span>
          <div className={styles.shareLinks}>
            <a href={`https://twitter.com/intent/tweet?text=${SHARE_TEXT}&url=${SHARE_URL}`} target="_blank" rel="noreferrer">X</a>
            <a href={`mailto:?subject=${encodeURIComponent('Supporting Revive Worship')}&body=${SHARE_TEXT}%20${SHARE_URL}`}>Email</a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer">Instagram</a>
            <a href={TIKTOK_URL} target="_blank" rel="noreferrer">TikTok</a>
          </div>
        </div>

        <p className={styles.disclosure}>{TAX_DISCLOSURE}</p>
      </div>
    </section>
  );
}
